class NotificationPopup extends HTMLElement {
  constructor() {
    super();
    this.dismissedKeyBase = "montara-notification-popup-dismissed-until";
    this.subscribedKeyBase = "montara-notification-popup-subscribed";
    this.dismissedKey = this.dismissedKeyBase;
    this.subscribedKey = this.subscribedKeyBase;
    this.isOpen = false;
    this.showTimeout = null;
    this.hideTimeout = null;
    this.lastActiveElement = null;

    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.copyDiscountCode = this.copyDiscountCode.bind(this);
  }

  connectedCallback() {
    this.section = this.closest(".not-pu-section");
    if (!this.section) return;

    this.closeButtons = this.querySelectorAll("[data-popup-close]");
    this.copyButton = this.querySelector("[data-copy-discount]");
    this.discountCode = this.querySelector("[data-discount-code]")?.textContent?.trim();
    this.dialog = this.querySelector(".newsletter--popup");
    this.closeButton = this.querySelector(".newsletter__close-btn");
    this.emailField = this.querySelector('input[type="email"]');
    this.successField = this.querySelector(".form__success-field");
    this.errorMessage = this.querySelector(".newsletter-form__message");
    this.popupScope =
      this.dataset.popupScope || this.section.id || window.location.pathname || "global";
    this.dismissedKey = `${this.dismissedKeyBase}:${this.popupScope}`;
    this.subscribedKey = `${this.subscribedKeyBase}:${this.popupScope}`;

    if (this.errorMessage instanceof HTMLElement) {
      this.errorMessage.setAttribute("tabindex", "-1");
    }

    this.designMode = this.dataset.designMode === "true";
    this.hasSubscription = this.dataset.hasSubscription === "true";
    this.triggerMode = this.dataset.triggerMode || "either";
    this.delayMs = Math.max(
      0,
      Number.parseInt(this.dataset.delaySeconds || "5", 10) * 1000,
    );
    this.scrollThreshold = Math.max(
      0,
      Math.min(100, Number.parseInt(this.dataset.scrollThreshold || "40", 10)),
    );
    this.hideDays = Math.max(
      1,
      Number.parseInt(this.dataset.hideDays || "7", 10),
    );

    this.formHasSuccess = Boolean(this.querySelector(".form__success-field"));
    this.formHasError = Boolean(this.querySelector(".newsletter-form__message"));

    this.autoCopy = this.dataset.autoCopy === "true";
    this.autoApply = this.dataset.autoApply === "true";
    this.discountApplyUrl = this.dataset.discountApplyUrl || "";

    this.closeButtons.forEach((button) => {
      button.addEventListener("click", this.handleCloseClick);
    });

    if (this.copyButton) {
      this.copyButton.addEventListener("click", this.copyDiscountCode);
    }

    if (this.designMode) {
      this.show();
      return;
    }

    if (this.formHasSuccess) {
      this.setStoredValue(this.subscribedKey, "true");
      this.show();

      if (this.autoCopy && this.discountCode) {
        this.copyDiscountCode({ silent: true }).catch(() => {});
      }

      if (this.autoApply && this.discountApplyUrl) {
        window.setTimeout(() => {
          window.location.href = this.discountApplyUrl;
        }, 1500);
      }

      return;
    }

    if (this.formHasError) {
      this.show();
      return;
    }

    if (this.hasSubscription || this.getStoredValue(this.subscribedKey) === "true") {
      return;
    }

    if (this.isDismissed()) {
      return;
    }

    this.setupTriggers();
  }

  disconnectedCallback() {
    this.cleanupTriggers();
    document.removeEventListener("keydown", this.handleKeydown);
    if (this.hideTimeout) {
      window.clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    this.closeButtons?.forEach((button) => {
      button.removeEventListener("click", this.handleCloseClick);
    });

    if (this.copyButton) {
      this.copyButton.removeEventListener("click", this.copyDiscountCode);
    }
  }

  setupTriggers() {
    if (this.triggerMode === "delay" || this.triggerMode === "either") {
      this.showTimeout = window.setTimeout(() => {
        this.show();
      }, this.delayMs);
    }

    if (this.triggerMode === "scroll" || this.triggerMode === "either") {
      window.addEventListener("scroll", this.handleScroll, { passive: true });
      this.handleScroll();
    }
  }

  cleanupTriggers() {
    if (this.showTimeout) {
      window.clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }

    window.removeEventListener("scroll", this.handleScroll);
  }

  handleScroll() {
    if (this.isOpen) return;

    const scrollableHeight =
      document.documentElement.scrollHeight - window.innerHeight;
    const scrollProgress =
      scrollableHeight <= 0
        ? 100
        : (window.scrollY / scrollableHeight) * 100;

    if (scrollProgress >= this.scrollThreshold) {
      this.show();
    }
  }

  handleCloseClick(event) {
    event.preventDefault();

    const persistSubscribed =
      this.formHasSuccess || this.getStoredValue(this.subscribedKey) === "true";

    this.hide({
      persistDismissal: !persistSubscribed,
      persistSubscribed,
    });
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.hide({ persistDismissal: true });
    }
  }

  show() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.cleanupTriggers();
    if (this.hideTimeout) {
      window.clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
    this.section.classList.remove("hidden");
    this.section.setAttribute("aria-hidden", "false");
    if (!this.designMode) {
      const activeElement = document.activeElement;
      this.lastActiveElement =
        activeElement instanceof HTMLElement && !this.contains(activeElement)
          ? activeElement
          : null;
      document.documentElement.classList.add("notification-popup-open");
      document.body.classList.add("notification-popup-open");
    }
    document.addEventListener("keydown", this.handleKeydown);

    window.requestAnimationFrame(() => {
      this.section.classList.add("is-visible");
      this.focusDialog();
    });
  }

  hide({ persistDismissal = false, persistSubscribed = false } = {}) {
    if (!this.isOpen && !this.designMode) {
      if (persistDismissal) this.markDismissed();
      if (persistSubscribed) this.setStoredValue(this.subscribedKey, "true");
      return;
    }

    if (persistDismissal) {
      this.markDismissed();
    }

    if (persistSubscribed) {
      this.setStoredValue(this.subscribedKey, "true");
    }

    this.isOpen = false;
    this.section.classList.remove("is-visible");
    this.section.setAttribute("aria-hidden", "true");
    if (!this.designMode) {
      if (typeof removeTrapFocus === "function") {
        removeTrapFocus(this.lastActiveElement);
      } else if (this.lastActiveElement) {
        this.lastActiveElement.focus();
      }
      document.documentElement.classList.remove("notification-popup-open");
      document.body.classList.remove("notification-popup-open");
    }
    document.removeEventListener("keydown", this.handleKeydown);

    this.hideTimeout = window.setTimeout(() => {
      if (!this.isOpen && !this.designMode) {
        this.section.classList.add("hidden");
      }
    }, 320);
  }

  focusDialog() {
    if (this.designMode || !this.dialog) return;

    const focusTarget =
      this.successField ||
      this.errorMessage ||
      this.emailField ||
      this.closeButton ||
      this.dialog;

    if (!(focusTarget instanceof HTMLElement)) return;

    window.requestAnimationFrame(() => {
      if (!this.isOpen) return;

      if (typeof trapFocus === "function") {
        trapFocus(this.dialog, focusTarget);
      } else {
        focusTarget.focus();
      }
    });
  }

  markDismissed() {
    const expiresAt = Date.now() + this.hideDays * 24 * 60 * 60 * 1000;
    this.setStoredValue(this.dismissedKey, String(expiresAt));
  }

  isDismissed() {
    const expiresAt = Number.parseInt(this.getStoredValue(this.dismissedKey), 10);

    if (!expiresAt) return false;

    if (Date.now() > expiresAt) {
      this.removeStoredValue(this.dismissedKey);
      return false;
    }

    return true;
  }

  async copyDiscountCode(event) {
    if (!this.discountCode) return;

    const silent = event && typeof event === "object" && event.silent === true;

    try {
      await navigator.clipboard.writeText(this.discountCode);
    } catch (error) {
      if (!silent) {
        console.error("Failed to copy discount code", error);
      }
      return;
    }

    if (silent || !this.copyButton) return;

    const copyLabel = this.copyButton.dataset.copyLabel || this.copyButton.textContent;
    const successLabel = this.copyButton.dataset.copySuccessLabel || "Copied";
    this.copyButton.textContent = successLabel;
    window.setTimeout(() => {
      this.copyButton.textContent = copyLabel;
    }, 1800);
  }

  getStoredValue(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  setStoredValue(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      console.error("Failed to persist popup state", error);
    }
  }

  removeStoredValue(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      console.error("Failed to clear popup state", error);
    }
  }
}

if (!customElements.get("notification-popup")) {
  customElements.define("notification-popup", NotificationPopup);
}